// src/oura/ui/Controls.tsx
//
// The controls: the segmented track, the status key that filters, pill
// buttons and links, and the blue arrow link.

import { useRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type KeyboardEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRightIcon, Loader2Icon, type LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { DOT_TONE, FOCUS_RING, ROW_STRETCH, TYPE, type Tone } from "./tokens";
import { EngineLogo, type EngineKey } from "./EngineLogo";

/* ------------------------------------------------------------------------- *
 * Seg: the Apple segmented track.
 * ------------------------------------------------------------------------- */

export interface SegItem<K extends string> {
    key: K;
    label: ReactNode;
    icon?: LucideIcon;
    /** Trailing ink4 count. */
    count?: number;
    /** How many of those are new (not opened yet): a blue count after it. 0 or absent draws nothing. */
    fresh?: number;
    /** Leading engine mark, hidden below 640px where the track is tight. */
    logo?: EngineKey;
}

// Inset focus ring: the track scrolls sideways on a phone, and an outline
// drawn outside the button would be clipped by it.
const SEG_FOCUS = "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-600";

/**
 * `as="tablist"` gives tabs (role=tab, aria-selected, arrow keys move and
 * select); the caller renders the matching role="tabpanel". The default
 * `as="group"` is a filter: buttons with aria-pressed.
 *
 * `panelId` (tabs only, opt in) links the tabs to that panel: each tab gets
 * the id `${panelId}-tab-${key}` and aria-controls={panelId}, so the panel
 * can name itself with aria-labelledby.
 */
export function Seg<K extends string>({
    items,
    value,
    onChange,
    size = "md",
    ariaLabel,
    as = "group",
    panelId,
    className,
}: {
    items: SegItem<K>[];
    value: K;
    onChange: (key: K) => void;
    size?: "md" | "sm";
    ariaLabel: string;
    as?: "tablist" | "group";
    panelId?: string;
    className?: string;
}) {
    const tabs = as === "tablist";
    const buttons = useRef<Array<HTMLButtonElement | null>>([]);

    const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
        if (!tabs || (e.key !== "ArrowRight" && e.key !== "ArrowLeft")) return;
        e.preventDefault();
        const next = (i + (e.key === "ArrowRight" ? 1 : items.length - 1)) % items.length;
        buttons.current[next]?.focus();
        onChange(items[next].key);
    };

    return (
        <div
            role={as}
            aria-label={ariaLabel}
            className={cn("inline-flex max-w-full gap-0.5 overflow-x-auto rounded-[10px] bg-gray-100 p-0.5 scrollbar-hide", className)}
        >
            {items.map((item, i) => {
                const on = item.key === value;
                const Icon = item.icon;
                return (
                    <button
                        key={item.key}
                        ref={(el) => {
                            buttons.current[i] = el;
                        }}
                        type="button"
                        role={tabs ? "tab" : undefined}
                        id={tabs && panelId ? `${panelId}-tab-${item.key}` : undefined}
                        aria-controls={tabs && panelId ? panelId : undefined}
                        aria-selected={tabs ? on : undefined}
                        aria-pressed={tabs ? undefined : on}
                        tabIndex={tabs && !on ? -1 : undefined}
                        onClick={() => onChange(item.key)}
                        onKeyDown={(e) => onKeyDown(e, i)}
                        className={cn(
                            "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg font-medium transition-colors",
                            size === "sm" ? "h-[26px] px-2.5 text-xs" : cn("h-[30px] px-3", TYPE.tc),
                            on
                                ? "bg-white text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.04)]"
                                : "text-gray-500 hover:text-gray-900",
                            SEG_FOCUS,
                        )}
                    >
                        {item.logo && <EngineLogo engine={item.logo} size={14} className="hidden sm:block" />}
                        {Icon && <Icon aria-hidden size={14} />}
                        {item.label}
                        {item.count !== undefined && <span className="tabular-nums text-gray-400">{item.count}</span>}
                        {item.fresh ? (
                            <span className="grid h-4 min-w-4 place-items-center rounded-full bg-blue-600 px-1 text-[11px] font-semibold leading-none tabular-nums text-white">
                                {item.fresh}
                                <span className="sr-only"> new</span>
                            </span>
                        ) : null}
                    </button>
                );
            })}
        </div>
    );
}

/* ------------------------------------------------------------------------- *
 * StatusKey: a status legend that is also the filter.
 * ------------------------------------------------------------------------- */

export interface StatusKeyGroup {
    key: string;
    label: string;
    tone: Tone;
    count: number;
}

/**
 * "All N" first, then each non-empty group with its dot and count. Pressing
 * the pressed pill again returns to All. `value` is "all" or a group key; if
 * the chosen group empties, the caller resets to "all".
 *
 * "All" counts the groups added up. Where that sum is not one thing (groups
 * that overlap, or a group that counts something else), the caller passes
 * `total`: a number to show instead, or null to show "All" with no count.
 */
export function StatusKey({
    groups,
    value,
    onChange,
    ariaLabel = "Filter by status",
    total: totalProp,
    className,
}: {
    groups: StatusKeyGroup[];
    value: string;
    onChange: (key: string) => void;
    ariaLabel?: string;
    total?: number | null;
    className?: string;
}) {
    const total = totalProp === undefined ? groups.reduce((n, g) => n + g.count, 0) : totalProp;
    const chip = (key: string, label: string, count: number | null, tone?: Tone) => {
        const on = value === key;
        return (
            <button
                key={key}
                type="button"
                aria-pressed={on}
                onClick={() => onChange(on ? "all" : key)}
                className={cn(
                    TYPE.tc,
                    // 36px on a phone, where it is tapped; 30px beside a pointer.
                    "inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-3 font-medium transition-colors sm:h-[30px]",
                    on ? "bg-gray-900 text-white" : "bg-white text-gray-900 ring-1 ring-inset ring-gray-200 hover:bg-gray-50",
                    FOCUS_RING,
                )}
            >
                {tone && <span aria-hidden className={cn("h-2 w-2 rounded-full", DOT_TONE[tone])} />}
                {label}
                {count !== null && <span className={cn("tabular-nums", on ? "text-white/70" : "text-gray-400")}>{count}</span>}
            </button>
        );
    };
    return (
        <div role="group" aria-label={ariaLabel} className={cn("flex flex-wrap gap-2", className)}>
            {chip("all", "All", total)}
            {groups.filter((g) => g.count > 0).map((g) => chip(g.key, g.label, g.count, g.tone))}
        </div>
    );
}

/* ------------------------------------------------------------------------- *
 * Pills
 * ------------------------------------------------------------------------- */

export type PillVariant = "primary" | "secondary" | "text";

const PILL_VARIANT: Record<PillVariant, string> = {
    primary: "bg-blue-600 text-white hover:bg-blue-700 disabled:hover:bg-blue-600",
    secondary: "bg-white text-gray-900 ring-1 ring-inset ring-black/[0.08] hover:bg-gray-50 disabled:hover:bg-white",
    text: "text-blue-600 hover:underline disabled:hover:no-underline",
};

function pillClass(variant: PillVariant, size: "md" | "sm", className?: string): string {
    return cn(
        "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40",
        // The text pill is a link-weight control: its own height and no side
        // padding, with a 44px target drawn round it for a thumb.
        variant === "text"
            ? cn("relative h-6 px-1 after:absolute after:-inset-x-1 after:-inset-y-2.5 after:content-['']", TYPE.tc)
            // The filled pills keep their 28/32px look but take a 44px-tall
            // target for a thumb, the same way the text pill does.
            : size === "sm"
                ? "relative h-7 px-3 text-xs after:absolute after:inset-x-0 after:-inset-y-2 after:content-['']"
                : cn("relative h-8 px-4 after:absolute after:inset-x-0 after:-inset-y-1.5 after:content-['']", TYPE.tc),
        PILL_VARIANT[variant],
        FOCUS_RING,
        className,
    );
}

type PillOwnProps = {
    variant?: PillVariant;
    icon?: LucideIcon;
    size?: "md" | "sm";
};

/** 32px rounded-full button. `busy` swaps the icon for a spinner and disables it. */
export function PillButton({
    variant = "secondary",
    icon: Icon,
    size = "md",
    busy,
    className,
    children,
    type = "button",
    disabled,
    ...rest
}: PillOwnProps & { busy?: boolean } & ButtonHTMLAttributes<HTMLButtonElement>) {
    return (
        <button type={type} disabled={disabled || busy} className={pillClass(variant, size, className)} {...rest}>
            {busy ? <Loader2Icon aria-hidden size={14} className="animate-spin" /> : Icon && <Icon aria-hidden size={14} />}
            {children}
        </button>
    );
}

/** A pill that navigates: a router link with `to`, a plain anchor with `href`. */
export function PillLink({
    to,
    href,
    variant = "secondary",
    icon: Icon,
    size = "md",
    className,
    children,
    ...rest
}: PillOwnProps & { to?: string; href?: string } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href">) {
    const cls = pillClass(variant, size, className);
    const body = (
        <>
            {Icon && <Icon aria-hidden size={14} />}
            {children}
        </>
    );
    if (to) {
        return (
            <Link to={to} className={cls} {...rest}>
                {body}
            </Link>
        );
    }
    return (
        <a href={href} className={cls} {...rest}>
            {body}
        </a>
    );
}

/**
 * 13px medium blue text with a chevron: a router link, an anchor, or a
 * button. The text stays 18px tall; a pseudo-element gives it a 44px target.
 */
export function ArrowLink({
    to,
    href,
    onClick,
    children,
    className,
    newTab,
    ariaLabel,
    stretch,
}: {
    to?: string;
    href?: string;
    onClick?: () => void;
    children: ReactNode;
    className?: string;
    /** With `href`: opens in a new tab, as a link out of Oura (a shared doc) does. */
    newTab?: boolean;
    ariaLabel?: string;
    /**
     * The row's own action: its hit area stretches over the whole row (the
     * nearest positioned parent, a ROW_HOVER row), so the row is clicked
     * anywhere. Other controls in the row sit above it with ROW_OWN.
     */
    stretch?: boolean;
}) {
    const cls = cn(
        TYPE.tc,
        "inline-flex items-center gap-1 rounded font-medium text-blue-600 hover:text-blue-700",
        stretch
            ? cn(ROW_STRETCH, "group-hover:text-blue-700 [&>svg]:transition-transform group-hover:[&>svg]:translate-x-0.5")
            : "relative after:absolute after:-inset-x-3 after:-inset-y-[13px] after:content-['']",
        FOCUS_RING,
        className,
    );
    const body = (
        <>
            {children}
            <ChevronRightIcon aria-hidden size={12} className="shrink-0" />
        </>
    );
    if (to) {
        return (
            <Link to={to} className={cls}>
                {body}
            </Link>
        );
    }
    if (href) {
        return (
            <a href={href} onClick={onClick} className={cls} aria-label={ariaLabel} {...(newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
                {body}
            </a>
        );
    }
    return (
        <button type="button" onClick={onClick} className={cls}>
            {body}
        </button>
    );
}
